import React, { useState, useEffect } from "react";
import { motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Gamepad2, Sparkles } from "lucide-react";
import BlurText from "../components/BlurText";
import FadeContent from "../components/FadeContent";

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isCarouselPaused, setIsCarouselPaused] = useState(false);

  const carouselImages = [
    {
      src: "/assets/3D%20renderings/3D%20rendering%201.jpeg",
      alt: "Immersive VR gaming center with futuristic equipment and colorful lighting",
    },
    {
      src: "/assets/3D%20renderings/3D%20rendering%202.jpeg",
      alt: "Multiple VR gaming stations with advanced motion simulators in action",
    },
    {
      src: "/assets/3D%20renderings/3D%20rendering%203.jpeg",
      alt: "Modern VR entertainment facility featuring UFO-style and flight simulator pods",
    },
    {
      src: "/assets/3D%20renderings/3D%20rendering%204.jpeg",
      alt: "Interactive VR lounge with comfortable seating and state-of-the-art gaming equipment",
    },
  ];

  const gameBanners = [
    "/assets/Banners/banner1.jpeg",
    "/assets/Banners/banner2.jpeg",
    "/assets/Banners/banner3.jpeg",
    "/assets/Banners/banner4.jpeg",
    "/assets/Banners/banner11.jpeg",
  ];

  const vrMachines = [
    {
      name: "360° VR - Most Intense Experience",
      image: "/assets/Machines/360double-flight-vr.png",
      category: "Extreme Adventure",
      ageLimit: "6+",
      duration: "10-15 min",
      thrill: "Extreme",
      capacity: "Solo or 2-player competition",
      games: "3 styles of experiences",
      price: "25 coins",
      features: "Actually flips you upside down! Roller coaster journeys, air combat with real flips - our most intense 5D machine!"
    },
    {
      name: "Motorcycle Racing VR",
      image: "/assets/Machines/360 Moto.png",
      category: "Racing Competition",
      ageLimit: "8+",
      duration: "10-15 min",
      thrill: "High",
      capacity: "Solo or 2-person racing",
      games: "Racing & solo adventures",
      price: "20 coins",
      features: "Two motorcycles - race against each other or enjoy solo adventures with realistic bike controls"
    },
    {
      name: "Racing Car Simulator",
      image: "/assets/Machines/Car Similutor.png",
      category: "Racing Simulation",
      ageLimit: "Must reach gas pedal",
      duration: "10-15 min",
      thrill: "Medium",
      capacity: "Individual",
      games: "Multiple racing experiences",
      price: "20 coins",
      features: "Realistic car racing - as long as you can reach the gas pedal, you can drive!"
    },
    {
      name: "Action Pro VR",
      image: "/assets/Machines/Corps Pro.png",
      category: "Action Adventure",
      ageLimit: "6+",
      duration: "10-15 min",
      thrill: "High",
      capacity: "Solo or 2-player competition",
      games: "65+ action games",
      price: "25 coins",
      features: "Two machines - play solo or compete against each other in tactical gameplay and intense action adventures"
    },
    {
      name: "UFO Experience - The WOW Factory",
      image: "/assets/Machines/UFO.jpeg",
      category: "Family Adventure",
      ageLimit: "4+",
      duration: "10-15 min",
      thrill: "Dreamy to Thrilling",
      capacity: "Up to 5 people",
      games: "125+ experiences",
      price: "20 coins",
      features: "Our most popular! Dreamy & magical for kids, adventurous options, or scary thrills. Perfect for families!"
    },
  ];

  const toys = [
    {
      name: "Robot Builder Kit",
      image: "/assets/Toys/sample%20robot%20upsale%201.jpeg",
      description: "Build & Program Your Own Robot",
    },
    {
      name: "AI Coding Companion",
      image: "/assets/Toys/sample%20robot%20upsale%202.jpeg",
      description: "Learn Coding with Interactive AI",
    },
    {
      name: "Smart Circuit Board",
      image: "/assets/Toys/sample%20robot%20upsale%203.jpeg",
      description: "Electronics & Engineering Fun",
    },
    {
      name: "VR Creation Kit",
      image: "/assets/Toys/sample%20robot%20upsale%204.jpeg",
      description: "Design Your Own VR Worlds",
    },
    {
      name: "Drone Assembly Lab",
      image: "/assets/Toys/sample%20robot%20upsale%205.jpeg",
      description: "Build & Fly Your Custom Drone",
    },
  ];

  useEffect(() => {
    if (isCarouselPaused) return;

    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [isCarouselPaused, carouselImages.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide(
      (prev) => (prev - 1 + carouselImages.length) % carouselImages.length
    );
  };

  return (
    <main className="min-h-screen pt-20 sm:pt-24">
      {/* Skip Links for Screen Readers and Keyboard Users */}
      <nav className="sr-only focus:not-sr-only" role="navigation" aria-label="Skip navigation links">
        <a 
          href="#main-content" 
          className="absolute left-4 top-4 z-50 bg-blue-600 text-white px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 transform -translate-y-12 focus:translate-y-0 transition-transform duration-200"
        >
          Skip to main content
        </a>
        <a 
          href="#games" 
          className="absolute left-4 top-16 z-50 bg-blue-600 text-white px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 transform -translate-y-12 focus:translate-y-0 transition-transform duration-200"
        >
          Skip to games section
        </a>
        <a 
          href="#vr-machines" 
          className="absolute left-4 top-28 z-50 bg-blue-600 text-white px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 transform -translate-y-12 focus:translate-y-0 transition-transform duration-200"
        >
          Skip to VR machines
        </a>
      </nav>

      {/* Hero Section with Carousel */}
      <section
        id="main-content"
        className="hero-section relative min-h-screen overflow-hidden flex items-center"
        onMouseEnter={() => setIsCarouselPaused(true)}
        onMouseLeave={() => setIsCarouselPaused(false)}
        role="banner"
        aria-label="Welcome to Immersa VR Entertainment Center"
      >
        {/* Background Carousel */}
        <div className="absolute inset-0" role="img" aria-label="VR experience showcase carousel">
          {carouselImages.map((image, index) => (
            <motion.div
              key={index}
              className={`absolute inset-0 ${
                index === currentSlide ? "opacity-100" : "opacity-0"
              }`}
              initial={{ opacity: 0, scale: 1.1 }}
              animate={{
                opacity: index === currentSlide ? 1 : 0,
                scale: index === currentSlide ? 1 : 1.1,
              }}
              transition={{ duration: 1.5, ease: "easeOut" }}
              aria-hidden={index !== currentSlide}
            >
              <img
                src={image.src}
                alt={image.alt}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.src =
                    "https://via.placeholder.com/1200x800/9333ea/ffffff?text=VR+Experience";
                }}
              />
              {/* Enhanced gradient overlays for better text visibility and depth */}
              <div className="absolute inset-0 bg-gradient-to-br from-purple-900/70 via-blue-900/60 to-black/80" aria-hidden="true" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" aria-hidden="true" />
              {/* Animated gradient accent */}
              <motion.div 
                className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-pink-500/20"
                animate={{ 
                  opacity: [0.3, 0.5, 0.3],
                  scale: [1, 1.05, 1]
                }}
                transition={{ duration: 4, repeat: Infinity }}
                aria-hidden="true" 
              />
            </motion.div>
          ))}

          {/* Carousel Controls */}
          <motion.button
            onClick={prevSlide}
            className="absolute left-6 top-1/2 transform -translate-y-1/2 z-30 bg-white/20 hover:bg-white/30 focus:bg-white/40 backdrop-blur-sm rounded-full p-3 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-transparent"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            aria-label="Previous image"
            type="button"
          >
            <ChevronLeft className="w-6 h-6 text-white" aria-hidden="true" />
          </motion.button>

          <motion.button
            onClick={nextSlide}
            className="absolute right-6 top-1/2 transform -translate-y-1/2 z-30 bg-white/20 hover:bg-white/30 focus:bg-white/40 backdrop-blur-sm rounded-full p-3 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-transparent"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            aria-label="Next image"
            type="button"
          >
            <ChevronRight className="w-6 h-6 text-white" aria-hidden="true" />
          </motion.button>

          {/* Indicators */}
          <div className="absolute bottom-8 right-8 flex space-x-2 z-20" role="group" aria-label="Carousel indicators">
            {carouselImages.map((_, index) => (
              <motion.button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-transparent ${
                  index === currentSlide
                    ? "bg-white scale-125"
                    : "bg-white/50 hover:bg-white/75"
                }`}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.8 }}
                aria-label={`Go to slide ${index + 1} of ${carouselImages.length}`}
                aria-current={index === currentSlide ? "true" : "false"}
                type="button"
              />
            ))}
          </div>
        </div>

        {/* Hero Content - Left Aligned on Desktop, Centered on Mobile */}
        <div className="relative z-20 w-full py-8 sm:py-12">
          <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 w-full hero-content">
            <div className="text-center max-w-4xl mx-auto">
              <motion.div
                className="mb-3 sm:mb-4"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.2, ease: "easeOut" }}
              >
                {/* Top tagline with fire emojis - Enhanced */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8, y: -20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3, type: "spring", stiffness: 100 }}
                  className="mb-4 sm:mb-6"
                >
                  <motion.p 
                    className="inline-block text-base sm:text-xl md:text-2xl lg:text-3xl font-black bg-gradient-to-r from-yellow-200 via-yellow-300 to-orange-400 bg-clip-text text-transparent tracking-wide sm:tracking-widest px-3 py-1.5 sm:px-6 sm:py-2 rounded-full backdrop-blur-sm border border-yellow-400/50 drop-shadow-2xl"
                    style={{ textShadow: '0 0 30px rgba(250, 204, 21, 0.6), 0 4px 20px rgba(0, 0, 0, 0.9)' }}
                    animate={{ 
                      scale: [1, 1.05, 1],
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    🔥 FUTURE OF FUN! 🔥
                  </motion.p>
                </motion.div>

                {/* Main Title - IMMERSA Logo with glow effect */}
                {/* <motion.div 
                  className="flex justify-center mb-3 sm:mb-6"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 1, delay: 0.5, type: "spring", stiffness: 80 }}
                >
                  <motion.div
                    animate={{ 
                      filter: [
                        'drop-shadow(0 0 25px rgba(168, 85, 247, 0.7))',
                        'drop-shadow(0 0 45px rgba(168, 85, 247, 0.9))',
                        'drop-shadow(0 0 25px rgba(168, 85, 247, 0.7))'
                      ]
                    }}
                    transition={{ duration: 3, repeat: Infinity }}
                  >
                    <img
                      src="/assets/immersa logo.svg"
                      alt="Immersa Logo"
                      className="w-48 sm:w-80 md:w-96 lg:w-[32rem] h-auto"
                    />
                  </motion.div>
                </motion.div> */}

                {/* Subtitle - Enhanced */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.8 }}
                  className="mb-3 sm:mb-6 px-2"
                >
                  <h2 className="text-lg sm:text-3xl md:text-4xl lg:text-5xl font-black bg-gradient-to-r from-purple-300 via-pink-300 to-cyan-300 bg-clip-text text-transparent mb-1.5 sm:mb-4 tracking-wide sm:tracking-wider leading-tight" style={{ textShadow: '0 0 30px rgba(192, 132, 250, 0.5), 0 4px 15px rgba(0, 0, 0, 0.9)' }}>
                    5D VIRTUAL REALITY STATION
                  </h2>
                  <p className="text-xs sm:text-xl md:text-2xl text-cyan-100 font-semibold tracking-wide leading-relaxed drop-shadow-lg">
                    Cutting-edge VR Headsets & Immersive Motion Machines
                  </p>
                </motion.div>

                {/* Audience-Specific Callouts */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, delay: 1.5 }}
                  className="my-4 sm:my-10"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    {/* Individuals and Families - Enhanced */}
                    <motion.div 
                      className="relative group cursor-pointer overflow-hidden rounded-2xl border-2 border-blue-400/50 hover:border-blue-400/70 bg-gradient-to-br from-blue-600/30 via-purple-600/30 to-blue-700/30 backdrop-blur-sm transition-all duration-500"
                      whileHover={{ scale: 1.05, y: -5 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => navigate('/experience')}
                      transition={{ type: "spring", stiffness: 300 }}
                      animate={{
                        boxShadow: [
                          '0 0 20px rgba(96, 165, 250, 0.3)',
                          '0 0 30px rgba(96, 165, 250, 0.5)',
                          '0 0 20px rgba(96, 165, 250, 0.3)'
                        ]
                      }}
                    >
                      {/* Animated gradient overlay - always visible on mobile */}
                      <motion.div 
                        className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-purple-500/10 group-hover:from-blue-500/20 group-hover:to-purple-500/20 transition-all duration-500"
                        animate={{
                          opacity: [0.5, 1, 0.5]
                        }}
                        transition={{ duration: 3, repeat: Infinity }}
                      />
                      
                      <div className="relative p-6 sm:p-8 text-center">
                        {/* Animated emoji */}
                        <motion.div 
                          className="text-4xl sm:text-5xl mb-3 sm:mb-4"
                          animate={{ 
                            rotate: [0, -10, 10, -10, 0],
                            scale: [1, 1.1, 1]
                          }}
                          transition={{ 
                            duration: 2, 
                            repeat: Infinity,
                            repeatDelay: 1
                          }}
                        >
                          🎮
                        </motion.div>
                        
                        <h3 className="text-xl sm:text-2xl md:text-3xl font-black text-white mb-3 sm:mb-4 drop-shadow-lg">
                          Play & Explore
                        </h3>
                        <p className="text-sm sm:text-base md:text-lg text-white/90 leading-relaxed mb-4 sm:mb-6">
                          Experience thrilling VR games and rides for individuals and families. Perfect for a fun day out!
                        </p>
                        
                        <motion.div
                          className="inline-flex items-center space-x-2 text-white font-bold text-base sm:text-lg group-hover:text-blue-200 transition-colors"
                          whileHover={{ x: 5 }}
                        >
                          <span>Explore Experiences</span>
                          <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                        </motion.div>
                      </div>
                      
                      {/* Shine effect */}
                      <motion.div
                        className="absolute inset-0 -translate-x-full group-hover:translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent transition-transform duration-1000"
                      />
                    </motion.div>

                    {/* Birthday Parties - Enhanced */}
                    <motion.div 
                      className="relative group cursor-pointer overflow-hidden rounded-2xl border-2 border-pink-400/50 hover:border-pink-400/70 bg-gradient-to-br from-pink-600/30 via-red-600/30 to-pink-700/30 backdrop-blur-sm transition-all duration-500"
                      whileHover={{ scale: 1.05, y: -5 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => navigate('/birthday-parties')}
                      transition={{ type: "spring", stiffness: 300 }}
                      animate={{
                        boxShadow: [
                          '0 0 20px rgba(244, 114, 182, 0.3)',
                          '0 0 30px rgba(244, 114, 182, 0.5)',
                          '0 0 20px rgba(244, 114, 182, 0.3)'
                        ]
                      }}
                    >
                      {/* Animated gradient overlay - always visible on mobile */}
                      <motion.div 
                        className="absolute inset-0 bg-gradient-to-br from-pink-500/10 to-red-500/10 group-hover:from-pink-500/20 group-hover:to-red-500/20 transition-all duration-500"
                        animate={{
                          opacity: [0.5, 1, 0.5]
                        }}
                        transition={{ duration: 3, repeat: Infinity }}
                      />
                      
                      <div className="relative p-6 sm:p-8 text-center">
                        {/* Animated emoji */}
                        <motion.div 
                          className="text-4xl sm:text-5xl mb-3 sm:mb-4"
                          animate={{ 
                            rotate: [0, -10, 10, -10, 0],
                            scale: [1, 1.1, 1]
                          }}
                          transition={{ 
                            duration: 2, 
                            repeat: Infinity,
                            repeatDelay: 1
                          }}
                        >
                          🎂
                        </motion.div>
                        
                        <h3 className="text-xl sm:text-2xl md:text-3xl font-black text-white mb-3 sm:mb-4 drop-shadow-lg">
                          Birthday Parties
                        </h3>
                        <p className="text-sm sm:text-base md:text-lg text-white/90 leading-relaxed mb-4 sm:mb-6">
                          Make your child's birthday unforgettable with our immersive VR party packages for families and friends.
                        </p>
                        
                        <motion.div
                          className="inline-flex items-center space-x-2 text-white font-bold text-base sm:text-lg group-hover:text-pink-200 transition-colors"
                          whileHover={{ x: 5 }}
                        >
                          <span>Learn More</span>
                          <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                        </motion.div>
                      </div>
                      
                      {/* Shine effect */}
                      <motion.div
                        className="absolute inset-0 -translate-x-full group-hover:translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent transition-transform duration-1000"
                      />
                    </motion.div>
                  </div>
                </motion.div>
              {/* Trust Indicators */}
              <motion.div
                className="mb-6 sm:mb-8 hero-stats"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 2.4 }}
              >
                <p className="text-white/80 mb-4 text-center lg:text-left text-sm sm:text-base font-medium">
                  Trusted by over 2,400+ families!
                </p>
                <div className="mobile-stats flex flex-col sm:flex-row justify-center lg:justify-start items-center gap-6 sm:gap-8 px-4 sm:px-0">
                  {[
                    {
                      value: "4.9/5",
                      label: "Average Rating",
                      color: "text-purple-400",
                    },
                    { value: "500+", label: "Reviews", color: "text-blue-400" },
                    {
                      value: "95%",
                      label: "Return Rate",
                      color: "text-green-400",
                    },
                  ].map((stat, index) => (
                    <motion.div
                      key={index}
                      className="text-center bg-black/20 backdrop-blur-sm rounded-lg px-4 py-3 min-w-[100px]"
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5, delay: 2.6 + index * 0.1 }}
                    >
                      <div
                        className={`text-xl sm:text-2xl md:text-3xl font-bold ${stat.color} mb-1`}
                      >
                        {stat.value}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-300 font-medium">
                        {stat.label}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* CTA Buttons */}
              <motion.div
                className="hero-buttons flex flex-col gap-4 sm:gap-4 px-4 sm:px-0 max-w-lg mx-auto lg:mx-0"
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 2.8 }}
              >
                {/* Primary CTA */}
                <motion.div
                  className="relative group w-full"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <button
                    onClick={() => navigate("/waiver")}
                    className="mobile-button w-full inline-flex items-center justify-center space-x-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 focus:from-purple-700 focus:to-blue-700 text-white px-6 sm:px-8 py-4 rounded-full text-base sm:text-lg font-semibold transition-all duration-300 shadow-xl focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-transparent"
                    aria-label="Start waiver process to begin your VR experience"
                    type="button"
                  >
                    <span>📋 Start Waiver - Your VR Passport</span>
                    <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform duration-300" />
                  </button>
                </motion.div>

                {/* Secondary CTA */}
                <motion.div
                  className="relative group w-full"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <button
                    onClick={() =>
                      document
                        .getElementById("games")
                        ?.scrollIntoView({ behavior: "smooth" })
                    }
                    className="mobile-button w-full inline-flex items-center justify-center space-x-2 bg-transparent hover:bg-white/10 focus:bg-white/10 border-2 border-white/50 hover:border-white focus:border-white text-white px-6 sm:px-8 py-4 rounded-full text-base sm:text-lg font-semibold transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-transparent"
                    aria-label="Scroll to explore VR games section"
                    type="button"
                  >
                    <span>Explore VR Games</span>
                    <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform duration-300" />
                  </button>
                </motion.div>
              </motion.div>
            </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Games and Fun for Everyone Section */}
      <section 
        id="games" 
        className="py-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"
        aria-label="Games and fun for everyone"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <motion.div
              className="inline-flex items-center justify-center space-x-4 mb-6"
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <motion.div
                className="bg-gradient-to-r from-green-500 to-blue-500 rounded-full p-3"
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              >
                <Gamepad2 className="w-8 h-8 text-white" />
              </motion.div>
            </motion.div>

            <h2>
              <BlurText
                text="Games and Fun for Everyone"
                className="text-3xl md:text-4xl font-bold text-white mb-4"
                animateBy="words"
                delay={100}
              />
            </h2>

            <motion.p
              className="text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1 }}
              viewport={{ once: true }}
            >
              Discover exciting VR games and activities designed to entertain
              and educate
            </motion.p>
          </motion.div>

          {/* Game Banners Carousel */}
          <div className="relative max-w-6xl mx-auto overflow-hidden">
            <motion.div
              className="flex gap-4 sm:gap-6"
              animate={{
                x: [0, -100 * gameBanners.length]
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: "linear"
              }}
            >
              {/* Duplicate banners for seamless loop */}
              {[...gameBanners, ...gameBanners].map((banner, index) => (
                <motion.div
                  key={index}
                  className="flex-shrink-0 w-48 sm:w-56 md:w-64 lg:w-72 aspect-video relative rounded-xl overflow-hidden"
                  whileHover={{ 
                    scale: 1.05,
                    y: -8,
                    transition: { duration: 0.3 }
                  }}
                >
                  <img
                    src={banner}
                    alt={`Game Banner ${(index % gameBanners.length) + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.src = `https://via.placeholder.com/300x200/8b5cf6/ffffff?text=Game+${
                        (index % gameBanners.length) + 1
                      }`;
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  
                  {/* Play button overlay */}
                  <motion.div
                    className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    whileHover={{ scale: 1.1 }}
                  >
                    <div className="bg-white/20 backdrop-blur-sm rounded-full p-4 border border-white/30">
                      <div className="w-0 h-0 border-l-8 border-l-white border-t-6 border-t-transparent border-b-6 border-b-transparent ml-1"></div>
                    </div>
                  </motion.div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* VR Machines Section */}
      <section
        id="vr-machines"
        className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden"
        aria-label="Featured VR machines"
      >
        {/* Background Effects - Reduced for Performance */}
        <motion.div
          className="absolute inset-0 opacity-5"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 0.05 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
        >
          <div className="absolute top-20 left-10 w-64 h-64 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl" />
          <div className="absolute bottom-20 right-10 w-64 h-64 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl" />
        </motion.div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <motion.div
              className="inline-flex items-center justify-center space-x-4 mb-6"
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <motion.div
                className="bg-gradient-to-r from-purple-500 to-blue-500 rounded-full p-4"
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              >
                <Gamepad2 className="w-10 h-10 text-white" />
              </motion.div>
            </motion.div>

            <h2>
              <BlurText
                text="Our 5D VR Experience Machines"
                className="text-4xl md:text-5xl font-bold text-white mb-4 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent"
                animateBy="words"
                delay={100}
              />
            </h2>

            <motion.p
              className="text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1 }}
              viewport={{ once: true }}
            >
              Welcome to our VR station where virtual reality combines with actual and physical elements to create a 5D experience! 
              It's not just the headset - the seats move, machines tilt you, and you feel wind effects and real motion. 
              Each experience usually lasts 10-15 minutes and is packed with WOW moments!
            </motion.p>
          </motion.div>

          <div className="vr-machines-grid">
            {vrMachines.map((machine, index) => (
              <FadeContent
                key={machine.name}
                className="group bg-gradient-to-br from-slate-800/90 via-slate-700/90 to-slate-800/90 backdrop-blur-md rounded-2xl p-6 border border-slate-600/50 hover:border-purple-400/50 transition-all duration-300 hover:shadow-xl hover:shadow-purple-500/20 w-full max-w-sm"
                delay={index * 0.1}
              >
                <div className="aspect-[4/3] mb-4 overflow-hidden rounded-xl bg-gradient-to-br from-slate-700/50 to-purple-500/20 flex items-center justify-center p-4 group-hover:from-purple-500/30 group-hover:to-blue-500/30 transition-all duration-300 relative">
                  <img
                    src={machine.image}
                    alt={machine.name}
                    className="w-full h-full object-contain filter group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      const target = e.currentTarget;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        const fallback = parent.querySelector('.fallback-content') as HTMLElement;
                        if (fallback) {
                          fallback.style.display = 'flex';
                        }
                      }
                    }}
                  />
                  {/* Fallback content when image fails to load */}
                  <div 
                    className="fallback-content absolute inset-0 hidden items-center justify-center bg-gradient-to-br from-purple-600/20 to-blue-600/20 rounded-xl"
                  >
                    <div className="text-center">
                      <Gamepad2 className="w-16 h-16 text-purple-400 mx-auto mb-2" />
                      <p className="text-purple-300 text-sm font-medium">{machine.category}</p>
                    </div>
                  </div>
                </div>

                <div className="text-center space-y-3">
                  <h3 className="text-white font-bold text-lg leading-tight group-hover:text-purple-300 transition-colors duration-300">
                    {machine.name}
                  </h3>
                  <p className="text-gray-400 text-sm group-hover:text-gray-300 transition-colors duration-300">
                    {machine.category}
                  </p>

                  {/* Capacity and Games Info */}
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-center gap-2 flex-wrap">
                      <span className="bg-purple-500/20 px-2 py-1 rounded-full text-purple-300 font-medium">
                        {machine.capacity}
                      </span>
                      <span className="bg-cyan-500/20 px-2 py-1 rounded-full text-cyan-300 font-medium">
                        {machine.games}
                      </span>
                    </div>
                    
                    {/* Price */}
                    <div className="text-center">
                      <span className="bg-gradient-to-r from-yellow-400/20 to-orange-400/20 px-3 py-1 rounded-full text-yellow-300 font-bold text-sm">
                        {machine.price}
                      </span>
                    </div>
                  </div>

                  {/* Machine Details */}
                  <div className="flex justify-center gap-2 text-xs">
                    <span className="bg-blue-500/20 px-2 py-1 rounded-full text-blue-300 font-medium">
                      {machine.ageLimit}
                    </span>
                    <span className="bg-green-500/20 px-2 py-1 rounded-full text-green-300 font-medium">
                      {machine.duration}
                    </span>
                    <span
                      className={`px-2 py-1 rounded-full font-medium ${
                        machine.thrill === "High"
                          ? "bg-red-500/20 text-red-300"
                          : machine.thrill === "Medium"
                          ? "bg-yellow-500/20 text-yellow-300"
                          : machine.thrill === "Low"
                          ? "bg-green-500/20 text-green-300"
                          : "bg-gray-500/20 text-gray-300"
                      }`}
                    >
                      {machine.thrill}
                    </span>
                  </div>

                  {/* Features */}
                  <p className="text-xs text-gray-400 leading-relaxed px-2 group-hover:text-gray-300 transition-colors duration-300">
                    {machine.features}
                  </p>

                  <div className="w-16 h-1 bg-gradient-to-r from-purple-400 to-blue-400 mx-auto rounded-full opacity-60 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
              </FadeContent>
            ))}
          </div>
        </div>
      </section>

      {/* Family & Group Recommendations */}
      <section className="py-12 bg-gradient-to-br from-blue-900/20 to-purple-900/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-r from-cyan-500/10 to-purple-500/10 rounded-2xl p-8 border border-cyan-400/30">
              <div className="text-4xl mb-4">🛸✨</div>
              <h3 className="text-2xl font-bold text-white mb-4">For Families & Groups: Start with the UFO!</h3>
              <p className="text-lg text-gray-300 mb-4">
                Our most popular choice! The UFO can fit up to 5 people at the same time, making it perfect 
                for families and groups of friends. With 125+ experiences ranging from dreamy and magical 
                (great for younger kids) to adventurous and even scary options.
              </p>
              <div className="flex justify-center items-center space-x-6 text-sm text-gray-300">
                <div className="text-center">
                  <div className="text-cyan-400 font-semibold">👶 Ages 4+</div>
                  <div>Perfect for little ones</div>
                </div>
                <div className="text-center">
                  <div className="text-blue-400 font-semibold">👨‍👩‍👧‍👦 Up to 5 People</div>
                  <div>Share the experience</div>
                </div>
                <div className="text-center">
                  <div className="text-purple-400 font-semibold">🤩 Most Common Reaction</div>
                  <div>"WOW!" - Our WOW Factory</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Quick Overview */}
      <section className="py-16 bg-gradient-to-br from-purple-900/30 to-blue-900/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-white mb-4">💰 Coin Packages - Deals For Every Player!</h2>
            <p className="text-xl text-gray-300">Fantastic bundles and discounts to maximize your playtime!</p>
          </motion.div>

          {/* Basic Pricing */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8 max-w-2xl mx-auto">
            <div className="bg-white/5 rounded-xl p-6 shadow-md text-center">
              <div className="text-2xl mb-2">🎮</div>
              <div className="text-lg font-semibold text-white mb-1">Most Experiences</div>
              <div className="text-3xl font-bold text-blue-400 mb-1">20 coins</div>
              <div className="text-sm text-white/70">8-15 minutes each</div>
            </div>
            <div className="bg-white/5 rounded-xl p-6 shadow-md text-center">
              <div className="text-2xl mb-2">🚀</div>
              <div className="text-lg font-semibold text-white mb-1">360 & Action Pro</div>
              <div className="text-3xl font-bold text-purple-400 mb-1">25 coins</div>
              <div className="text-sm text-white/70">Premium experiences</div>
            </div>
          </div>

          {/* Popular Bundles */}
          <div className="mb-8">
            <h3 className="text-2xl font-bold text-white mb-6 text-center">🎁 Popular Bundles</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
              <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-2 border-purple-400/50 rounded-xl p-4 text-center relative">
                <div className="absolute -top-2 -right-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-black text-xs font-bold rounded-full px-2 py-1">
                  Save $5
                </div>
                <div className="text-xl mb-1">🥉</div>
                <div className="text-lg font-bold text-white">40 Coins</div>
                <div className="text-sm text-gray-300">$35.00</div>
              </div>
              <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-2 border-purple-400/50 rounded-xl p-4 text-center relative">
                <div className="absolute -top-2 -right-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-black text-xs font-bold rounded-full px-2 py-1">
                  Save $30
                </div>
                <div className="text-xl mb-1">🥈</div>
                <div className="text-lg font-bold text-white">100 Coins</div>
                <div className="text-sm text-gray-300">$70.00</div>
              </div>
              <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-2 border-purple-400/50 rounded-xl p-4 text-center relative">
                <div className="absolute -top-2 -right-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-black text-xs font-bold rounded-full px-2 py-1">
                  Save $70
                </div>
                <div className="text-xl mb-1">🥇</div>
                <div className="text-lg font-bold text-white">200 Coins</div>
                <div className="text-sm text-gray-300">$130.00</div>
              </div>
              <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-2 border-purple-400/50 rounded-xl p-4 text-center relative">
                <div className="absolute -top-2 -right-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-black text-xs font-bold rounded-full px-2 py-1">
                  save $110
                </div>
                <div className="text-xl mb-1">🏆</div>
                <div className="text-lg font-bold text-white">300 Coins</div>
                <div className="text-sm text-gray-300">$190.00</div>
              </div>
            </div>
          </div>

          {/* Special Offers */}
          <div className="bg-gradient-to-r from-cyan-500/10 to-purple-500/10 rounded-xl p-6 border border-cyan-400/20 mb-6 max-w-4xl mx-auto">
            <div className="text-center">
              <h3 className="text-xl font-bold text-white mb-4">🎟️ How It Works: Play Card System</h3>
              <p className="text-gray-300 mb-4">
                Get your play card from the front desk, then buy coins at the kiosk. 
                Your play card stays active for 1 year! 📅
              </p>
              <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-300">
                <div>
                  <div className="text-green-400 font-semibold">🖤 Black Friday Special!</div>
                  <div>Extra promotional discounts</div>
                </div>
                <div>
                  <div className="text-blue-400 font-semibold">💳 Better Bundles</div>
                  <div>More coins = better discounts</div>
                </div>
                <div>
                  <div className="text-pink-400 font-semibold">⏰ Best Time</div>
                  <div>Now is the best time to get coins!</div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center">
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <button
                onClick={() => navigate("/experience")}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-4 rounded-xl font-semibold text-lg shadow-lg transition-all duration-300 mr-4"
              >
                🎮 View All Coin Packages
              </button>
              <button
                onClick={() => navigate("/waiver")}
                className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white px-8 py-4 rounded-xl font-semibold text-lg shadow-lg transition-all duration-300"
              >
                🚀 Load Coins & Start Playing!
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Our VR Experience is Unique */}
      <section 
        id="unique-experience"
        className="py-16 bg-gradient-to-br from-indigo-900/20 via-purple-900/20 to-blue-900/20 relative overflow-hidden"
        aria-label="Why our VR experience is unique"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2>
              <BlurText 
                text="Beyond Basic VR Headsets"
                className="text-3xl md:text-4xl font-bold text-white mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent"
                animateBy="words"
                delay={100}
              />
            </h2>
            
            <motion.p 
              className="text-lg text-gray-300 max-w-4xl mx-auto leading-relaxed mb-8"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1 }}
              viewport={{ once: true }}
            >
              Our specialized VR equipment creates experiences that go far beyond what you'd get with a basic VR headset or even real-life activities like driving or riding a motorcycle.
            </motion.p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: "🎵",
                title: "Immersive Audio",
                description: "3D surround sound, dynamic music, and realistic sound effects that respond to your movements"
              },
              {
                icon: "💡",
                title: "Dynamic Lighting",
                description: "LED lighting systems that sync with the game, creating atmospheric effects impossible in real life"
              },
              {
                icon: "🎮",
                title: "Motion Simulation",
                description: "Hydraulic systems, wind effects, and haptic feedback that make you feel every twist and turn"
              },
              {
                icon: "👥",
                title: "Multiplayer Features",
                description: "Compete with friends, team up for missions, or race against players in ways reality can't match"
              }
            ].map((feature, index) => (
              <motion.div 
                key={index}
                className="bg-gradient-to-br from-slate-800/80 to-slate-700/80 backdrop-blur-sm rounded-xl p-6 border border-slate-600/50 text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05, transition: { duration: 0.3 } }}
              >
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-white font-bold text-lg mb-2">{feature.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div 
            className="text-center mt-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            viewport={{ once: true }}
          >
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              <span className="text-purple-400 font-semibold">The result?</span> An experience that combines the safety and convenience of virtual reality with sensory enhancements that make it more thrilling than the real thing!
            </p>
          </motion.div>
        </div>
      </section>

      {/* STEM Toys Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        <motion.div
          className="absolute inset-0 opacity-10"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 0.1 }}
          transition={{ duration: 1 }}
        >
          <motion.div
            className="absolute top-32 left-16 w-80 h-80 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl"
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 180, 360],
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </motion.div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <motion.div
              className="inline-flex items-center justify-center space-x-4 mb-6"
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <motion.div
                className="bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full p-4"
                animate={{
                  boxShadow: [
                    "0 0 20px rgba(234, 179, 8, 0.5)",
                    "0 0 40px rgba(234, 179, 8, 0.8)",
                    "0 0 20px rgba(234, 179, 8, 0.5)",
                  ],
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Sparkles className="w-10 h-10 text-white" />
              </motion.div>
            </motion.div>

            <BlurText
              text="Coming Soon: Toys, STEM & More!"
              className="text-4xl md:text-5xl font-bold text-white mb-4 bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent"
              animateBy="words"
              delay={100}
            />

            <motion.p
              className="text-lg text-gray-300 max-w-3xl mx-auto"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1 }}
              viewport={{ once: true }}
            >
              Educational toys and STEM activities that make learning an
              adventure
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-8">
            {toys.map((toy, index) => (
              <motion.div
                key={index}
                className="group bg-gradient-to-br from-yellow-900/40 via-orange-900/40 to-red-900/40 backdrop-blur-md rounded-2xl p-6 border border-yellow-400/20 hover:border-yellow-400/60 transition-all duration-500"
                initial={{ opacity: 0, y: 50, rotate: -5 }}
                whileInView={{ opacity: 1, y: 0, rotate: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{
                  y: -10,
                  rotate: 2,
                  scale: 1.05,
                  boxShadow: "0 25px 50px -12px rgba(234, 179, 8, 0.25)",
                }}
              >
                <div className="aspect-square mb-4 overflow-hidden rounded-xl bg-gradient-to-br from-white/10 to-yellow-500/10 flex items-center justify-center p-4 group-hover:bg-gradient-to-br group-hover:from-yellow-500/20 group-hover:to-orange-500/20 transition-all duration-500">
                  <motion.img
                    src={toy.image}
                    alt={toy.name}
                    className="w-full h-full object-contain filter group-hover:drop-shadow-lg"
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.3 }}
                    onError={(e) => {
                      e.currentTarget.src = `https://via.placeholder.com/200x200/f59e0b/ffffff?text=STEM+Toy+${
                        index + 1
                      }`;
                    }}
                  />
                </div>

                <div className="text-center">
                  <h3 className="text-white font-bold text-lg leading-tight mb-2 group-hover:text-yellow-300 transition-colors duration-300">
                    {toy.name}
                  </h3>
                  <p className="text-gray-400 text-sm mb-3 group-hover:text-gray-300 transition-colors duration-300">
                    {toy.description}
                  </p>

                  <motion.button
                    className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300 opacity-0 group-hover:opacity-100"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    🔔 Notify When Available
                  </motion.button>

                  <div className="w-12 h-1 bg-gradient-to-r from-yellow-400 to-orange-400 mx-auto rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 mt-2"></div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stay Tuned for Workshops and Classes Section */}
      <section className="py-20 bg-gradient-to-br from-indigo-900/30 via-purple-900/30 to-pink-900/30 relative overflow-hidden">
        <motion.div
          className="absolute inset-0 opacity-10"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 0.1 }}
          transition={{ duration: 1 }}
        >
          <motion.div
            className="absolute top-20 left-16 w-96 h-96 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl"
            animate={{
              y: [0, -30, 0],
              x: [0, 20, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 12,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </motion.div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <BlurText
              text="Stay Tuned for Workshops and Classes!"
              className="text-4xl md:text-5xl font-bold text-white mb-8 bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent"
              animateBy="words"
              delay={100}
            />

            <motion.p
              className="text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed mb-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1 }}
              viewport={{ once: true }}
            >
              Experience our comprehensive workshops with our community of
              learners and future innovators!
            </motion.p>
          </motion.div>

          {/* Age Groups */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {[
              {
                title: "Ages 6-10",
                content: "VR Art Creation • Basic Robotics • Fun Coding Games",
                color: "from-blue-600/20 to-cyan-600/20",
                borderColor: "border-blue-400/30",
                textColor: "text-blue-300",
              },
              {
                title: "Ages 11-15",
                content: "Advanced Robotics • Game Design • AI Basics",
                color: "from-purple-600/20 to-pink-600/20",
                borderColor: "border-purple-400/30",
                textColor: "text-purple-300",
              },
              {
                title: "Ages 16+",
                content:
                  "VR Development • Engineering Projects • Tech Entrepreneurship",
                color: "from-green-600/20 to-emerald-600/20",
                borderColor: "border-green-400/30",
                textColor: "text-green-300",
              },
            ].map((group, index) => (
              <motion.div
                key={index}
                className={`bg-gradient-to-br ${group.color} rounded-xl p-6 border ${group.borderColor} backdrop-blur-md`}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05, y: -10 }}
              >
                <h4 className={`${group.textColor} font-bold text-xl mb-3`}>
                  {group.title}
                </h4>
                <p className="text-white text-sm leading-relaxed">
                  {group.content}
                </p>
              </motion.div>
            ))}
          </div>

          {/* STEM Overview */}
          <motion.div
            className="bg-gradient-to-br from-purple-900/50 via-blue-900/50 to-cyan-900/50 backdrop-blur-md rounded-3xl p-12 border border-purple-400/20 hover:border-purple-400/40 transition-all duration-500 group"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-bold text-white mb-6 text-center">
              Which Workshop Interests You Most?
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-8">
              {[
                { icon: "🔬", name: "Science", color: "text-cyan-300" },
                { icon: "💻", name: "Technology", color: "text-blue-300" },
                { icon: "⚙️", name: "Engineering", color: "text-purple-300" },
                { icon: "🎨", name: "Arts", color: "text-pink-300" },
                { icon: "📐", name: "Mathematics", color: "text-yellow-300" },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  className="text-center group-hover:scale-110 transition-transform duration-300"
                  style={{ transitionDelay: `${index * 100}ms` }}
                  whileHover={{ scale: 1.2 }}
                >
                  <div className="text-4xl mb-3">{item.icon}</div>
                  <p className={`${item.color} font-semibold`}>{item.name}</p>
                </motion.div>
              ))}
            </div>
            <div className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 rounded-2xl p-6 border border-white/10">
              <p className="text-lg text-white/90 font-light text-center">
                🌟 Interactive Learning • 🚀 Hands-on Experiments • 🎯
                Problem-Solving Adventures
              </p>
            </div>
            <div className="text-center">
              <motion.button
                className="mt-10 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                📧 Join Our Workshop Waitlist
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 pt-0 relative overflow-hidden bg-gradient-to-br from-purple-900/30 to-blue-900/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <BlurText
              text="What Our Players Say"
              className="text-4xl font-bold text-center mb-16 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent"
              animateBy="words"
              delay={100}
            />
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                name: "Sarah M.",
                initial: "S",
                rating: 5,
                text: "The VR experience was absolutely mind-blowing! My kids couldn't stop talking about the space exploration game. The STEM workshops were educational and fun!",
              },
              {
                name: "Michael R.",
                initial: "M",
                rating: 5,
                text: "As a birthday party venue, this place was perfect! Safe, engaging, and the staff made sure everyone had an amazing time. Highly recommend!",
              },
              {
                name: "Lisa K.",
                initial: "L",
                rating: 5,
                text: "The combination of VR gaming and educational STEM activities is genius. My daughter learned about robotics while having the time of her life!",
              },
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold">
                    {testimonial.initial}
                  </div>
                  <div className="ml-3">
                    <h4 className="font-semibold text-white">
                      {testimonial.name}
                    </h4>
                    <div className="flex text-yellow-400">
                      {"★".repeat(testimonial.rating)}
                    </div>
                  </div>
                </div>
                <p className="text-gray-300 italic">{testimonial.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Family Package Promotions */}
    </main>
  );
};

export default HomePage;
